import styled, { css } from 'styled-components';
import type { TypographyProps } from 'styled-system';
import { space, typography } from 'styled-system';

import type { SpaceProps } from '@/components/Box';
import { Box } from '@/components/Box';
import { Typography } from '@/components/Typography';
import type { FontSizes } from '@/theme/types/fontSizes';

import type {
  InputProps,
  InputSize,
  InputVariantProps,
} from './DeprecatedInput.types';
import { switchColors } from './utils';

export const defaultProps: Required<InputVariantProps> = {
  colorVariant: 'default',
  variant: 'underlined',
};

const inputFontSize = (fontSizes: FontSizes): Record<InputSize, string> => ({
  m: fontSizes.p2,
  l: fontSizes.p1,
});

interface WrapperProps extends Omit<InputProps, 'size'>, InputVariantProps {}
export const Wrapper = styled(Box)<WrapperProps & { size: InputSize }>`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  max-width: ${({ fullWidth, theme }) =>
    fullWidth ? '100%' : theme.components.input.wrapperWidth};
  height: ${({ size, theme }) => theme.components.input.height[size]};
  padding-left: ${({ theme }) => theme.space.s075};
  padding-right: ${({ theme }) => theme.space.s075};

  ${({ colorVariant, disabled, state, theme: { colors }, variant }) => css`
    ${switchColors(colors, colorVariant, variant, state, disabled)}
  `};

  ${({ theme, variant }) =>
    variant === 'boxedBold' &&
    css`
      border-color: ${theme.colors.primaryBlue};
      height: ${theme.components.select.variant.boxed.height};

      input {
        font-weight: bold;
        text-transform: uppercase;
        color: ${theme.colors.primaryBlue};
      }
    `}
`;

type InputStyleProps = TypographyProps | SpaceProps;

export const Input = styled.input<
  InputStyleProps & { hasPlaceholder: boolean; inputSize: InputSize }
>`
  ${space};
  ${typography};
  font-size: ${({ inputSize, theme }) =>
    inputFontSize(theme.fontSizes)[inputSize]};
  background: none;
  outline: none;
  border: none;
  padding: 0;
  width: 100%;
  &:focus {
    outline: 1px solid transparent;
  }
  ${({ hasPlaceholder, value }) =>
    !!value &&
    hasPlaceholder &&
    css`
      padding-top: 0.4rem;
      + span {
        opacity: 1;
        transform: translateY(-130%);
        font-size: 8px;
      }
    `}
`;

export const Placeholder = styled.span<{
  colorVariant?: string;
  isLeftContentVisible?: boolean;
  size: InputSize;
}>`
  position: absolute;
  left: ${({ isLeftContentVisible, theme }) =>
    isLeftContentVisible ? theme.space.s250 : theme.space.s075};
  font-size: ${({ size, theme }) => inputFontSize(theme.fontSizes)[size]};
  color: ${({ colorVariant, theme }) =>
    colorVariant === 'default'
      ? theme.colors.secondaryGrey
      : colorVariant === 'defaultInverted'
      ? theme.colors.whiteAlpha40
      : theme.colors.darkGrey};
  transition: 0.2s ease all;
  pointer-events: none;
`;

export const ErrorMessage = styled(Typography)`
  margin-top: ${({ theme }) => `-${theme.space.s075}`};
  font-size: ${({ theme }) => theme.fontSizes.p3};
  color: ${({ theme }) => theme.colors.error};
`;

export const LeftContentWrapper = styled(Box)`
  margin-right: ${({ theme }) => theme.space.s050};
  svg {
    color: ${({ theme }) => theme.colors.white};
  }
`;
